import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useDataProvider, useRecordContext } from 'react-admin'
import { Box, Typography, CircularProgress, TextField } from '@mui/material'

import { extractAudioPath } from '../utils/fileUploadHelpers'

const CustomAudioPlayerField = ({
  source,
  audioPath: propAudioPath,
  label,
  fullWidth = true,
  showInfo = true,
  variant = 'standard',
  ...props
}) => {
  const dataProvider = useDataProvider()
  const record = useRecordContext()
  const [signedUrl, setSignedUrl] = useState('')
  const [loading, setLoading] = useState(false)

  // Memoize the audioPath to prevent infinite loops when dealing with File objects
  const audioPath = useMemo(() => {
    const rawValue = propAudioPath || record?.[source]
    return extractAudioPath(rawValue)
  }, [propAudioPath, record, source])

  // Memoize the getSignedUrl function to prevent unnecessary re-creations
  const getSignedUrl = useCallback(
    async audioPath => {
      if (!audioPath) return ''

      // If it's already a blob URL (from File object), return it directly
      if (audioPath.startsWith('blob:')) {
        return audioPath
      }

      try {
        setLoading(true)
        const response = await dataProvider.create('ai-books/get-signed-url', {
          data: { audioPath },
        })
        return response.data.signedUrl || ''
      } catch (error) {
        console.error(`Error getting signed URL for ${source}:`, error)
        return ''
      } finally {
        setLoading(false)
      }
    },
    [dataProvider, source],
  )

  // Fetch signed URL when audioPath changes
  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (audioPath) {
        const url = await getSignedUrl(audioPath)
        setSignedUrl(url)
      } else {
        setSignedUrl('')
      }
    }
    fetchSignedUrl()
  }, [audioPath, getSignedUrl])

  return (
    <Box>
      {/* Display the audio path as text field */}
      {showInfo && (
        <TextField
          label={label}
          variant={variant}
          fullWidth={fullWidth}
          value={audioPath || ''}
          disabled
          {...props}
        />
      )}

      {/* Audio player section */}
      {audioPath && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Audio Player{' '}
            {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          {signedUrl && !loading ? (
            <audio controls style={{ width: '100%' }} preload="metadata">
              <source src={signedUrl} type="audio/mpeg" />
              <source src={signedUrl} type="audio/wav" />
              <source src={signedUrl} type="audio/ogg" />
              Your browser does not support the audio element.
            </audio>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Audio not available
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default CustomAudioPlayerField
